import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { bool, array } from 'prop-types';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes, LISTING_STATE_DRAFT, LISTING_STATE_CLOSED } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { sendContactMessage } from '../../util/api';

import { Page, CrownAuthShell, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import ContactForm from './ContactForm';
import css from './ContactPage.module.css';

// Build the human-readable picker option label for an own-listing summary:
// "{title}" + " — #{number}" when a legacy listing number exists + a
// parenthetical status when the listing isn't active (sold / on hold / draft /
// closed). Active/published listings get no status suffix.
const optionLabel = (listing, intl) => {
  const { title, state, listingStatus, listingNumber } = listing;
  const base = listingNumber ? `${title} — #${listingNumber}` : title;

  let statusId = null;
  if (state === LISTING_STATE_DRAFT) {
    statusId = 'ContactPage.statusDraft';
  } else if (listingStatus === 'sold') {
    statusId = 'ContactPage.statusSold';
  } else if (listingStatus === 'on_hold') {
    statusId = 'ContactPage.statusOnHold';
  } else if (state === LISTING_STATE_CLOSED) {
    statusId = 'ContactPage.statusClosed';
  }

  return statusId ? `${base} (${intl.formatMessage({ id: statusId })})` : base;
};

export const ContactPageComponent = props => {
  const { scrollingDisabled, currentUser, ownListings } = props;
  const intl = useIntl();

  // 'form' (default) | 'sent' (confirmation after a successful send)
  const [submitState, setSubmitState] = useState('form');
  const [inProgress, setInProgress] = useState(false);
  const [sendError, setSendError] = useState(false);

  const user = ensureCurrentUser(currentUser);
  const isAuthenticated = !!user.id;
  const userEmail = user.attributes?.email;

  // Picker: signed-in users with >=1 listing (any state, newest first).
  const showListingPicker = isAuthenticated && ownListings.length > 0;
  const listingOptions = ownListings.map(l => ({
    value: l.id,
    label: optionLabel(l, intl),
    title: l.title,
  }));

  const handleSubmit = values => {
    setInProgress(true);
    setSendError(false);

    const selected = listingOptions.find(o => o.value === values.listingId);
    const payload = {
      name: values.name,
      email: values.email,
      message: values.message,
      listingId: values.listingId || null,
      listingTitle: selected?.title || null,
      // Honeypot + Turnstile token — accepted now, enforced server-side in Phase 2.
      company: values.company || '',
      turnstileToken: values.turnstileToken || null,
    };

    return sendContactMessage(payload)
      .then(() => {
        setInProgress(false);
        setSubmitState('sent');
        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0);
        }
      })
      .catch(() => {
        setInProgress(false);
        setSendError(true);
      });
  };

  const isSent = submitState === 'sent';

  // Rail copy swaps between the two states (mirrors AuthenticationPage's
  // per-state shellProps approach).
  const shellProps = isSent
    ? {
        eyebrow: intl.formatMessage({ id: 'ContactPage.confirmEyebrow' }),
        headline: intl.formatMessage({ id: 'ContactPage.confirmHeadline' }),
      }
    : {
        eyebrow: intl.formatMessage({ id: 'ContactPage.eyebrow' }),
        headline: intl.formatMessage({ id: 'ContactPage.headline' }),
      };

  const schemaTitle = intl.formatMessage({ id: 'ContactPage.schemaTitle' });
  const schemaDescription = intl.formatMessage({ id: 'ContactPage.schemaDescription' });

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ContactPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={
          <div className={css.solidHeaderWrap}>
            <TopbarContainer variant="homepage" solid />
          </div>
        }
        footer={<FooterContainer />}
      >
        <CrownAuthShell backdrop="cream" {...shellProps}>
          {isSent ? (
            <div className={css.confirmBody}>
              <div className={css.seal}>
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#3a0104"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className={css.confirmH1}>
                <FormattedMessage id="ContactPage.confirmTitle" />
              </h2>
              <p className={css.confirmSub}>
                <FormattedMessage
                  id="ContactPage.confirmSub"
                  values={{ email: <b>help@premierhorse.com</b> }}
                />
              </p>
              <NamedLink name="SearchPage" className={css.browseBtn}>
                <FormattedMessage id="ContactPage.confirmBrowse" />
              </NamedLink>
              <NamedLink name="LandingPage" className={css.quiet}>
                <FormattedMessage id="ContactPage.confirmHome" />
              </NamedLink>
            </div>
          ) : (
            <ContactForm
              initialValues={{ email: userEmail }}
              inProgress={inProgress}
              sendError={sendError}
              showListingPicker={showListingPicker}
              listingOptions={listingOptions}
              onSubmit={handleSubmit}
            />
          )}
        </CrownAuthShell>
      </LayoutSingleColumn>
    </Page>
  );
};

ContactPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  ownListings: array,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { ownListings } = state.ContactPage;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    ownListings,
  };
};

const ContactPage = compose(connect(mapStateToProps))(ContactPageComponent);

export default ContactPage;
